import React, { useEffect, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Business as BrandIcon, Category as CategoryIcon, EditOutlined } from "@mui/icons-material";
import { Brand, Category, Product } from "../../../types";
import { productService } from "../../../services/productService";
import { SavePatch } from "./useProductQuickSave";

// The pick lists, loaded once per page visit (shared by every product).
let brandsCache: Promise<Brand[]> | null = null;
let categoriesCache: Promise<Category[]> | null = null;
const loadBrands = () =>
    (brandsCache ??= productService
        .getBrands({ limit: 1000, active_only: true })
        .then((b) => [...b].sort((x, y) => x.name.localeCompare(y.name)))
        .catch((err) => {
            brandsCache = null;
            throw err;
        }));
const loadCategories = () =>
    (categoriesCache ??= productService
        .getCategories({ limit: 1000, active_only: true })
        .then((c) => [...c].sort((x, y) => x.name.localeCompare(y.name)))
        .catch((err) => {
            categoriesCache = null;
            throw err;
        }));

interface Props {
    product: Product;
    editable: boolean;
    save: SavePatch;
    dirtyFor: (key: string) => (dirty: boolean) => void;
}

const NO_BRAND = { brand_id: "", name: "No brand" } as Brand;

/**
 * Brand, main category and extra categories, each changed in place: the
 * brand and main category save as soon as one is picked; extra categories
 * are picked together, then saved.
 */
export const ClassificationEditor: React.FC<Props> = ({ product, editable, save, dirtyFor }) => {
    const [editing, setEditing] = useState<"brand" | "category" | "extras" | null>(null);
    const [brands, setBrands] = useState<Brand[] | null>(null);
    const [categories, setCategories] = useState<Category[] | null>(null);
    const [extras, setExtras] = useState<Category[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    // Esc closes the extra-categories list first; only a second Esc cancels.
    const [extrasListOpen, setExtrasListOpen] = useState(false);

    useEffect(() => {
        setEditing(null);
        setError("");
    }, [product.product_id, product.brand_id, product.category_id, product.categories]);

    const extrasNow = (product.categories || []).filter((c) => c.category_id !== product.category_id);
    const extrasDirty =
        editing === "extras" &&
        extras.map((c) => c.category_id).sort().join() !== extrasNow.map((c) => c.category_id).sort().join();
    const reportDirty = dirtyFor("extras");
    useEffect(() => reportDirty(extrasDirty), [extrasDirty, reportDirty]);
    useEffect(() => () => reportDirty(false), [reportDirty]);

    const open = (what: "brand" | "category" | "extras") => {
        setError("");
        setEditing(what);
        if (what === "brand") loadBrands().then(setBrands).catch(() => setError("Couldn't load brands."));
        else loadCategories().then(setCategories).catch(() => setError("Couldn't load categories."));
        if (what === "extras") setExtras(extrasNow);
    };

    const run = async (patch: Parameters<SavePatch>[0], message: string) => {
        setBusy(true);
        setError("");
        const failed = await save(patch, message);
        setBusy(false);
        if (failed) setError(failed);
        else setEditing(null);
    };

    const pencil = (what: "brand" | "category" | "extras", label: string) =>
        editable && editing !== what ? (
            <Tooltip title={`Change ${label}`}>
                <IconButton size="small" onClick={() => open(what)} aria-label={`Change ${label}`} sx={{ p: { xs: 1.75, sm: 0.75 }, m: { xs: -1, sm: 0 }, my: { xs: -1.25, sm: -0.5 } }}>
                    <EditOutlined sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        ) : null;

    const cancelOnEsc = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            e.stopPropagation();
            setEditing(null);
        }
    };

    const labelSx = { minWidth: 92, flexShrink: 0 } as const;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {/* Brand */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 36 }}>
                <BrandIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" sx={labelSx}>
                    Brand
                </Typography>
                {editing === "brand" ? (
                    <Autocomplete
                        size="small"
                        fullWidth
                        openOnFocus
                        loading={!brands}
                        options={brands ? [NO_BRAND, ...brands] : []}
                        // Starts empty so typing searches straight away;
                        // the current brand shows as the placeholder.
                        value={null}
                        autoHighlight
                        getOptionLabel={(b) => b.name}
                        isOptionEqualToValue={(a, b) => a.brand_id === b.brand_id}
                        onChange={(_, b) => {
                            if (!b || b.brand_id === (product.brand_id ?? "")) return;
                            void run(
                                { brand_id: b.brand_id || (null as any) },
                                b.brand_id ? `Brand set to ${b.name}` : "Brand removed",
                            );
                        }}
                        onClose={(_, reason) => reason === "escape" && setEditing(null)}
                        onKeyDown={cancelOnEsc}
                        disabled={busy}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                autoFocus
                                placeholder={product.brand ? `${product.brand.name} — search to change` : "Search brands"}
                                onBlur={() => !busy && setEditing(null)}
                            />
                        )}
                    />
                ) : (
                    <>
                        {product.brand ? (
                            <Chip label={product.brand.name} size="small" variant="outlined" />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No brand
                            </Typography>
                        )}
                        {pencil("brand", "brand")}
                    </>
                )}
                {busy && editing === "brand" && <CircularProgress size={18} />}
            </Box>

            {/* Main category */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 36 }}>
                <CategoryIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" sx={labelSx}>
                    Category
                </Typography>
                {editing === "category" ? (
                    <Autocomplete
                        size="small"
                        fullWidth
                        openOnFocus
                        loading={!categories}
                        options={categories ?? []}
                        value={null}
                        autoHighlight
                        getOptionLabel={(c) => c.name}
                        isOptionEqualToValue={(a, b) => a.category_id === b.category_id}
                        onChange={(_, c) => {
                            if (!c || c.category_id === product.category_id) return;
                            void run({ category_id: c.category_id }, `Category set to ${c.name}`);
                        }}
                        onClose={(_, reason) => reason === "escape" && setEditing(null)}
                        onKeyDown={cancelOnEsc}
                        disabled={busy}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                autoFocus
                                placeholder={product.category ? `${product.category.name} — search to change` : "Search categories"}
                                onBlur={() => !busy && setEditing(null)}
                            />
                        )}
                    />
                ) : (
                    <>
                        {product.category ? (
                            <Chip label={product.category.name} size="small" color="primary" />
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No category
                            </Typography>
                        )}
                        {pencil("category", "category")}
                    </>
                )}
                {busy && editing === "category" && <CircularProgress size={18} />}
            </Box>

            {/* Extra categories */}
            <Box sx={{ display: "flex", alignItems: editing === "extras" ? "flex-start" : "center", gap: 1, minHeight: 36 }}>
                <CategoryIcon fontSize="small" color="disabled" sx={{ mt: editing === "extras" ? 1 : 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ ...labelSx, mt: editing === "extras" ? 1 : 0 }}>
                    Also in
                </Typography>
                {editing === "extras" ? (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            openOnFocus
                            disableCloseOnSelect
                            loading={!categories}
                            options={(categories ?? []).filter((c) => c.category_id !== product.category_id)}
                            value={extras}
                            onChange={(_, v) => setExtras(v)}
                            getOptionLabel={(c) => c.name}
                            isOptionEqualToValue={(a, b) => a.category_id === b.category_id}
                            onOpen={() => setExtrasListOpen(true)}
                            onClose={() => setExtrasListOpen(false)}
                            onKeyDown={(e) => {
                                if (e.key !== "Escape") return;
                                e.stopPropagation(); // never closes the drawer
                                if (!extrasListOpen) setEditing(null);
                            }}
                            disabled={busy}
                            renderInput={(params) => <TextField {...params} autoFocus placeholder="Add categories" />}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
                            <Button size="small" color="inherit" onClick={() => setEditing(null)} disabled={busy}>
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={busy || !extrasDirty}
                                onClick={() =>
                                    void run(
                                        { category_ids: extras.map((c) => c.category_id) },
                                        "Categories saved",
                                    )
                                }
                            >
                                {busy ? "Saving…" : "Save"}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                        {extrasNow.length > 0 ? (
                            extrasNow.map((c) => <Chip key={c.category_id} label={c.name} size="small" variant="outlined" />)
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No other categories
                            </Typography>
                        )}
                        {pencil("extras", "other categories")}
                    </Box>
                )}
            </Box>

            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default ClassificationEditor;
